import { AuthProvider } from "@pankod/refine-core";
import { nhost } from "utility";

// The nhost data provider rejects every GraphQL failure with a hardcoded
// `statusCode: 200` and no `status` field, and Hasura reports an expired session
// as a GraphQL error rather than an HTTP 401. So the only reliable way to tell a
// dead session apart from a permission denial is the code Hasura puts in
// `extensions`. handleError() spreads the GraphQLError[] onto the rejected
// object, so the original entries survive under numeric keys.
const isAuthError = (error: any): boolean => {
  if (error?.status === 401 || error?.statusCode === 401) {
    return true;
  }

  const hasInvalidJwtCode = Object.values(error ?? {}).some(
    (entry: any) => entry?.extensions?.code === "invalid-jwt"
  );
  if (hasInvalidJwtCode) {
    return true;
  }

  return (
    typeof error?.message === "string" &&
    /invalid-jwt|JWTExpired|Malformed Authorization header/i.test(error.message)
  );
};

export const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const { error } = await nhost.auth.signIn({
      email: username,
      password,
    });

    if (error) {
      return Promise.reject(error);
    }

    return Promise.resolve("/");
  },

  logout: async () => {
    const { error } = await nhost.auth.signOut();
    if (error) {
      return Promise.reject(error);
    }

    return Promise.resolve("/");
  },
  checkError: async (error) => {
    // Refine logs the user out whenever this rejects, and every data hook routes
    // its errors here. Permission denials, missing fields, validation failures
    // and network blips must therefore resolve — the calling hook surfaces them
    // as an error notification on the page instead.
    if (!isAuthError(error)) {
      return Promise.resolve();
    }

    await nhost.auth.refreshSession();

    // refreshSession() resolves void whether or not it succeeded, so confirm the
    // session actually came back before deciding to keep the user signed in.
    const isAuthenticated = await nhost.auth.isAuthenticatedAsync();
    if (!isAuthenticated) {
      // The rejected value is forwarded verbatim as useLogout's redirectPath.
      return Promise.reject("/login");
    }

    return Promise.resolve();
  },
  checkAuth: async () => {
    const pathname = window.location.pathname
    const publicScorecardPaths = ['/candidatereport/', '/centascorecard/preview/', '/download/scorecard-pdf']
    if (publicScorecardPaths.some(path => pathname.startsWith(path) || pathname === path.replace(/\/$/, ''))) {
      return Promise.resolve()
    }

    const isAuthenticated = await nhost.auth.isAuthenticatedAsync();
    if (isAuthenticated) {
      return Promise.resolve();
    }

    return Promise.reject();
  },
  getPermissions: () => {
    const user = nhost.auth.getUser();
    if (user) {
      return Promise.resolve(user.roles);
    }

    return Promise.resolve([]);
  },
  getUserIdentity: () => {
    const user = nhost.auth.getUser();
    if (user) {
      return Promise.resolve({
        ...user,
        name: user.displayName,
        avatar: user.avatarUrl,
      });
    }

    return Promise.resolve(null);
  },
};
